import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  ApiKeyAutoModelNamespaceRecord,
  ApiKeyModelMappingConflict,
  ApiKeyModelMappingRecord,
  ApiKeySummary,
  CatalogSku,
  CreateApiKeyRequest,
  UpdateApiKeyRequest,
} from '@nadoutong/contracts';
import { PlatformApiError } from '../../api/client.ts';
import { ModelMappingConflictError } from '../../api/keyModelMappings.ts';
import { useCatalog } from '../../api/useCatalog.ts';
import { useDisplayRate } from '../../api/useBilling.ts';
import {
  useApplyKeyModelNamespace,
  useDeleteKeyModelMapping,
  useDisableKeyAutoModelNamespace,
  useEnableKeyAutoModelNamespace,
  useKeyModelMappings,
  useUpsertKeyModelMapping,
} from '../../api/useKeyModelMappings.ts';
import {
  useCreateApiKey,
  useKeysList,
  useRevokeApiKey,
  useUpdateApiKey,
} from '../../api/useKeys.ts';
import { PanelError } from '../../components/PanelError.tsx';
import { coerceErrorMessage, useToast } from '../../components/Toast.tsx';
import {
  formatTimestamp,
  formatUsdCentsAsCny,
  usdCentsToCnyCents,
} from '../../components/formatters.ts';
import { useClipboard } from '../../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../../hooks/useSession.tsx';
import { useT } from '../../i18n/index.ts';
import type { I18nKey } from '../../i18n/index.ts';
import {
  Button,
  ConfirmDialog,
  Dialog,
  Drawer,
  EmptyState,
  FormField,
  Input,
  PageHeader,
  Select,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
} from '../../ui/index.ts';

/**
 * `/access/keys` — key management, and ONLY key management (epic #897
 * PR 5; channel-less since epic #898 P6 #913). Keys no longer bind to a
 * channel: model routing is per-key alias → SKU mappings edited in the
 * "Model mappings" drawer, and key creation optionally seeds those
 * mappings from a marketplace namespace.
 *
 * Interaction contract (epic #897 AC 5–7, carried forward):
 *   - Create and edit run in a `Drawer`.
 *   - Revoke is a danger `ConfirmDialog` with type-to-confirm on the
 *     key's name (prefix fallback).
 *   - The one-time plaintext reveal keeps its forced Dialog.
 *   - Errors are single-channel: validation + submit failures render
 *     inline; only success confirmations toast. The one exception is
 *     the post-create namespace snapshot: it runs behind the reveal
 *     dialog, so an atomic bulk failure reports via an error toast.
 */

type TranslateFn = ReturnType<typeof useT>;

function isValidDisplayRate(cnyPerUsd: number | null | undefined): cnyPerUsd is number {
  return typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
}

// Storage-autonomy P3 (#1410) §5.4 retired the per-request cap: it gated a
// pre-request cost estimate, and nothing estimates cost any more, so there is
// no moment at which it could fire. The API field and any stored value are
// untouched — a PATCH that omits it leaves the column alone — the console just
// stops offering to set it. The period budgets below survive because a window
// can still be cut off mid-flight; a single request cannot be un-served.
type KeyLimitPeriod = 'daily' | 'weekly' | 'monthly';

type KeyLimitDraft = Record<KeyLimitPeriod, string>;
type KeyLimitUsdCents = Record<KeyLimitPeriod, number | null>;

const KEY_LIMIT_PERIODS: readonly {
  readonly id: KeyLimitPeriod;
  readonly suffix: string;
  readonly labelKey: I18nKey;
  readonly placeholderKey: I18nKey;
}[] = [
  {
    id: 'daily',
    suffix: 'daily',
    labelKey: 'keys.limit.daily.label',
    placeholderKey: 'keys.limit.daily.placeholder',
  },
  {
    id: 'weekly',
    suffix: 'weekly',
    labelKey: 'keys.limit.weekly.label',
    placeholderKey: 'keys.limit.weekly.placeholder',
  },
  {
    id: 'monthly',
    suffix: 'monthly',
    labelKey: 'keys.limit.monthly.label',
    placeholderKey: 'keys.limit.monthly.placeholder',
  },
];

function emptyLimitDraft(): KeyLimitDraft {
  return { daily: '', weekly: '', monthly: '' };
}

function keyLimitValue(key: ApiKeySummary, period: KeyLimitPeriod): number | null {
  if (period === 'daily') return key.dailyLimitUsdCents;
  if (period === 'weekly') return key.weeklyLimitUsdCents;
  return key.monthlyLimitUsdCents;
}

function formatLimitInputValue(
  usdCents: number | null | undefined,
  cnyPerUsd: number | null | undefined,
): string {
  if (typeof usdCents !== 'number' || !Number.isFinite(usdCents)) return '';
  const cnyCents = usdCentsToCnyCents(usdCents, cnyPerUsd);
  if (cnyCents === null) return '';
  const yuan = (cnyCents / 100).toFixed(2);
  return yuan.endsWith('.00') ? yuan.slice(0, -3) : yuan;
}

function limitDraftFromKey(
  key: ApiKeySummary,
  cnyPerUsd: number | null | undefined,
): KeyLimitDraft {
  return {
    daily: formatLimitInputValue(key.dailyLimitUsdCents, cnyPerUsd),
    weekly: formatLimitInputValue(key.weeklyLimitUsdCents, cnyPerUsd),
    monthly: formatLimitInputValue(key.monthlyLimitUsdCents, cnyPerUsd),
  };
}

function parseLimitInput(
  raw: string,
  cnyPerUsd: number | null | undefined,
  t: TranslateFn,
): { ok: true; value: number | null } | { ok: false; message: string } {
  const trimmed = raw.trim();
  if (trimmed.length === 0) {
    return { ok: true, value: null };
  }
  if (!/^\d+(?:\.\d{1,2})?$/.test(trimmed)) {
    return { ok: false, message: t('keys.form.limit_invalid') };
  }
  if (!isValidDisplayRate(cnyPerUsd)) {
    return { ok: false, message: t('keys.form.limits_rate_unavailable') };
  }
  const parts = trimmed.split('.');
  const wholeRaw = parts[0] ?? '0';
  const fractionRaw = parts[1] ?? '';
  const cnyCents =
    Number.parseInt(wholeRaw, 10) * 100 + Number.parseInt(fractionRaw.padEnd(2, '0'), 10);
  if (!Number.isFinite(cnyCents) || cnyCents < 0) {
    return { ok: false, message: t('keys.form.limit_invalid') };
  }
  return { ok: true, value: Math.round(cnyCents / cnyPerUsd) };
}

function parseLimitDraft(
  draft: KeyLimitDraft,
  cnyPerUsd: number | null | undefined,
  t: TranslateFn,
): { ok: true; value: KeyLimitUsdCents } | { ok: false; message: string } {
  const parsed: KeyLimitUsdCents = { daily: null, weekly: null, monthly: null };
  for (const period of KEY_LIMIT_PERIODS) {
    const result = parseLimitInput(draft[period.id], cnyPerUsd, t);
    if (!result.ok) return result;
    parsed[period.id] = result.value;
  }
  return { ok: true, value: parsed };
}

function assignCreateLimitField(
  body: CreateApiKeyRequest,
  period: KeyLimitPeriod,
  value: number | null,
): void {
  if (value === null) return;
  if (period === 'daily') body.daily_limit_usd_cents = value;
  else if (period === 'weekly') body.weekly_limit_usd_cents = value;
  else body.monthly_limit_usd_cents = value;
}

function assignUpdateLimitField(
  body: UpdateApiKeyRequest,
  period: KeyLimitPeriod,
  value: number | null,
): void {
  if (period === 'daily') body.daily_limit_usd_cents = value;
  else if (period === 'weekly') body.weekly_limit_usd_cents = value;
  else body.monthly_limit_usd_cents = value;
}

function isOwnApiKey(
  key: ApiKeySummary,
  currentAccountId: string | null,
  currentRole: string | null,
): boolean {
  if (currentAccountId === null) return false;
  if (key.assignedAccountId === currentAccountId) return true;
  return key.assignedAccountId === null && currentRole === 'owner';
}

function formatApiError(err: unknown, t: TranslateFn): string {
  if (err instanceof PlatformApiError) {
    return `${err.code}: ${err.message}`;
  }
  return coerceErrorMessage(err, t('keys.form.network_error'));
}

/** Namespace-grouped view over the marketplace SKUs (epic #898 P6). */
interface SkuNamespaceGroup {
  readonly namespaceId: string;
  readonly displayName: string;
  readonly skus: readonly CatalogSku[];
}

function groupSkusByNamespace(skus: readonly CatalogSku[]): readonly SkuNamespaceGroup[] {
  const groups = new Map<string, { displayName: string; skus: CatalogSku[] }>();
  for (const sku of skus) {
    const group = groups.get(sku.namespace_id) ?? {
      displayName: sku.namespace_display_name,
      skus: [],
    };
    group.skus.push(sku);
    groups.set(sku.namespace_id, group);
  }
  return [...groups.entries()]
    .map(([namespaceId, group]) => ({
      namespaceId,
      displayName: group.displayName,
      skus: [...group.skus].sort((a, b) => a.model.localeCompare(b.model)),
    }))
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

function skuDisplayLabel(sku: CatalogSku): string {
  return sku.model_display_name ?? sku.model;
}

export function AccessKeys(): JSX.Element {
  const t = useT();
  const { user } = useSession();
  const toast = useToast();
  const currentAccountId = user?.accountId ?? null;
  const canManageKeys =
    hasPermission(user, 'org:api_keys:manage') || hasPermission(user, 'member:api_keys:manage');

  const keysQ = useKeysList();
  const displayRateQ = useDisplayRate();
  const revokeM = useRevokeApiKey();
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;

  const ownActiveKeys = useMemo(
    () =>
      (keysQ.data?.keys ?? [])
        .filter((key) => key.revokedAt === null)
        .filter((key) => isOwnApiKey(key, currentAccountId, user?.orgRole ?? null)),
    [keysQ.data, currentAccountId, user?.orgRole],
  );

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ApiKeySummary | null>(null);
  const [mappingsTarget, setMappingsTarget] = useState<ApiKeySummary | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<ApiKeySummary | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);
  const [createdPlaintext, setCreatedPlaintext] = useState<{
    readonly key: string;
    readonly name: string | null;
  } | null>(null);

  function closeRevoke(): void {
    setRevokeTarget(null);
    setRevokeError(null);
  }

  async function handleRevoke(): Promise<void> {
    if (revokeTarget === null) return;
    setRevokeError(null);
    try {
      await revokeM.mutateAsync(revokeTarget.id);
      closeRevoke();
      toast.success(t('keys.toast.revoked'));
    } catch (err) {
      setRevokeError(formatApiError(err, t));
    }
  }

  const revokePhrase =
    revokeTarget === null
      ? ''
      : revokeTarget.name !== null && revokeTarget.name.length > 0
        ? revokeTarget.name
        : revokeTarget.prefix;

  return (
    <section data-testid="keys-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('keys.title')}
        sub={t('keys.subtitle')}
        testId="keys-header"
        actions={
          canManageKeys ? (
            <Button
              variant="primary"
              onClick={() => setCreateOpen(true)}
              data-testid="keys-create-button"
            >
              {t('keys.create')}
            </Button>
          ) : undefined
        }
      />

      {keysQ.isLoading ? (
        <TableSkeleton rows={4} testId="keys-loading" />
      ) : keysQ.isError ? (
        <div role="alert" data-testid="keys-error" className="flex flex-wrap items-center gap-3">
          <p className="m-0 text-dense text-bad">{formatApiError(keysQ.error, t)}</p>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              void keysQ.refetch();
            }}
          >
            {t('keys.retry')}
          </Button>
        </div>
      ) : keysQ.data !== undefined && ownActiveKeys.length === 0 ? (
        <div className="rounded-lg border border-line-soft bg-card">
          <EmptyState
            testId="keys-empty"
            title={t('keys.empty.title')}
            hint={t('keys.empty.hint')}
            action={
              canManageKeys ? (
                <Button
                  variant="secondary"
                  onClick={() => setCreateOpen(true)}
                  data-testid="keys-empty-create"
                >
                  {t('keys.create')}
                </Button>
              ) : undefined
            }
          />
        </div>
      ) : keysQ.data !== undefined ? (
        <KeysTable
          keys={ownActiveKeys}
          cnyPerUsd={cnyPerUsd}
          canManage={canManageKeys}
          onEdit={(key) => setEditTarget(key)}
          onMappings={(key) => setMappingsTarget(key)}
          onRevoke={(key) => {
            setRevokeError(null);
            setRevokeTarget(key);
          }}
        />
      ) : null}

      {createOpen && canManageKeys ? (
        <KeyDrawer
          mode="create"
          target={null}
          onClose={() => setCreateOpen(false)}
          onCreated={(plaintext, name) => {
            setCreatedPlaintext({ key: plaintext, name });
          }}
        />
      ) : null}

      {editTarget !== null && canManageKeys ? (
        <KeyDrawer
          mode="edit"
          target={editTarget}
          onClose={() => setEditTarget(null)}
          onCreated={() => undefined}
        />
      ) : null}

      {mappingsTarget !== null ? (
        <KeyMappingsDrawer
          target={mappingsTarget}
          canManage={canManageKeys}
          onClose={() => setMappingsTarget(null)}
        />
      ) : null}

      {revokeTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !revokeM.isPending) closeRevoke();
          }}
          tone="danger"
          title={t('keys.revoke.title')}
          body={
            <div className="flex flex-col gap-2">
              <p className="m-0">
                {t('keys.revoke.description.before')}
                <span className="font-mono text-paper">
                  {revokeTarget.prefix.length > 0 ? `${revokeTarget.prefix}…` : revokeTarget.id}
                </span>
                {t('keys.revoke.description.after')}
              </p>
              {revokeError !== null ? (
                <p role="alert" data-testid="keys-revoke-error" className="m-0 text-label text-bad">
                  {revokeError}
                </p>
              ) : null}
            </div>
          }
          confirmLabel={t('keys.revoke.submit')}
          cancelLabel={t('keys.revoke.cancel')}
          closeLabel={t('keys.revoke.close')}
          typeToConfirm={revokePhrase}
          typeToConfirmLabel={t('keys.revoke.type_label')}
          loading={revokeM.isPending}
          testId="keys-revoke-dialog"
          onConfirm={() => {
            void handleRevoke();
          }}
        />
      ) : null}

      {createdPlaintext !== null ? (
        <KeyRevealDialog
          plaintext={createdPlaintext.key}
          keyName={createdPlaintext.name}
          onClose={() => setCreatedPlaintext(null)}
        />
      ) : null}
    </section>
  );
}

interface KeysTableProps {
  readonly keys: readonly ApiKeySummary[];
  readonly cnyPerUsd: number | null;
  readonly canManage: boolean;
  readonly onEdit: (key: ApiKeySummary) => void;
  readonly onMappings: (key: ApiKeySummary) => void;
  readonly onRevoke: (key: ApiKeySummary) => void;
}

function KeysTable({
  keys,
  cnyPerUsd,
  canManage,
  onEdit,
  onMappings,
  onRevoke,
}: KeysTableProps): JSX.Element {
  const t = useT();
  return (
    <Table testId="keys-table">
      <THead>
        <Tr interactive={false}>
          <Th>{t('keys.column.name')}</Th>
          <Th>{t('keys.column.prefix')}</Th>
          <Th>{t('keys.column.spend_limits')}</Th>
          <Th>{t('keys.column.created')}</Th>
          <Th>{t('keys.column.last_used')}</Th>
          <Th>{t('keys.column.actions')}</Th>
        </Tr>
      </THead>
      <TBody>
        {keys.map((key) => (
          <Tr key={key.id} data-testid={`keys-row-${key.id}`}>
            <Td emphasis heading data-testid={`keys-name-${key.id}`}>
              {key.name ?? '—'}
            </Td>
            <Td mono label={t('keys.column.prefix')}>
              {key.prefix.length > 0 ? `${key.prefix}…` : '—'}
            </Td>
            <Td label={t('keys.column.spend_limits')} data-testid={`keys-limit-${key.id}`}>
              <KeyLimitCell keySummary={key} cnyPerUsd={cnyPerUsd} />
            </Td>
            <Td mono label={t('keys.column.created')}>
              {formatTimestamp(key.createdAt)}
            </Td>
            <Td mono label={t('keys.column.last_used')}>
              {key.lastUsedAt !== null ? formatTimestamp(key.lastUsedAt) : t('keys.never_used')}
            </Td>
            <Td label={t('keys.column.actions')} stack>
              <div className="flex items-center justify-end gap-1.5 max-md:justify-start">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMappings(key)}
                  data-testid={`keys-mappings-${key.id}`}
                >
                  {t('keys.mappings.button')}
                </Button>
                {canManage ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onEdit(key)}
                      data-testid={`keys-edit-${key.id}`}
                    >
                      {t('keys.edit')}
                    </Button>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => onRevoke(key)}
                      data-testid={`keys-revoke-${key.id}`}
                    >
                      {t('keys.revoke')}
                    </Button>
                  </>
                ) : null}
              </div>
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}

function KeyLimitCell({
  keySummary,
  cnyPerUsd,
}: {
  readonly keySummary: ApiKeySummary;
  readonly cnyPerUsd: number | null;
}): JSX.Element {
  const t = useT();
  const set = KEY_LIMIT_PERIODS.filter((period) => keyLimitValue(keySummary, period.id) !== null);
  if (set.length === 0) {
    return <span className="text-paper-low">{t('keys.limit.none')}</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {set.map((period) => {
        const value = keyLimitValue(keySummary, period.id);
        return (
          <span
            key={period.id}
            data-testid={`keys-limit-${keySummary.id}-${period.suffix}`}
            className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-line-soft bg-well px-1.5 py-0.5 text-micro"
          >
            <span className="text-paper-low">{t(period.labelKey)}</span>
            <span className="font-mono text-paper-mid">
              {value === null ? '—' : formatUsdCentsAsCny(value, cnyPerUsd)}
            </span>
          </span>
        );
      })}
    </div>
  );
}

interface KeyDrawerProps {
  readonly mode: 'create' | 'edit';
  readonly target: ApiKeySummary | null;
  readonly onClose: () => void;
  readonly onCreated: (plaintext: string, name: string | null) => void;
}

/** Sentinel for the "no preset mappings" template option (Radix Select items need non-empty values). */
const TEMPLATE_NONE = 'none';

/**
 * Shared create/edit sheet. Mounted only while open so edit prefill
 * reads the target row once. Limits are string-diffed against the
 * prefill (edit) so the CNY↔USD round-trip can't produce phantom
 * changes; when the display-rate query fails the limits section is
 * disabled with an explicit notice instead of silently breaking
 * (epic #897 edge case).
 *
 * Since epic #898 P6 (#913) the create body sends NO `channel_id`.
 * Creation optionally applies a server-side "start from namespace"
 * snapshot after the key is created. The namespace endpoint validates
 * aliases and writes the full set atomically, so the browser never
 * performs a partially successful series of per-model PUTs.
 */
function KeyDrawer({ mode, target, onClose, onCreated }: KeyDrawerProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const createM = useCreateApiKey();
  const updateM = useUpdateApiKey();
  const applyNamespaceM = useApplyKeyModelNamespace();
  const catalogQ = useCatalog();
  const displayRateQ = useDisplayRate();

  const pending = createM.isPending || updateM.isPending || applyNamespaceM.isPending;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const limitsEditable = isValidDisplayRate(cnyPerUsd);

  const [name, setName] = useState(target?.name ?? '');
  const [templateNamespace, setTemplateNamespace] = useState(TEMPLATE_NONE);
  const [limitDraftState, setLimitDraftState] = useState<KeyLimitDraft | null>(null);
  const [nameError, setNameError] = useState<string | null>(null);
  const [limitsError, setLimitsError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  // Prefill limits reactively until first touch: the display rate can
  // resolve after the drawer mounts, and the CNY prefill needs it.
  const initialLimitDraft = useMemo<KeyLimitDraft>(
    () =>
      target !== null && limitsEditable ? limitDraftFromKey(target, cnyPerUsd) : emptyLimitDraft(),
    [target, limitsEditable, cnyPerUsd],
  );
  const limitDraft = limitDraftState ?? initialLimitDraft;

  const namespaceGroups = useMemo(
    () => groupSkusByNamespace(catalogQ.data?.skus ?? []),
    [catalogQ.data?.skus],
  );
  const selectedTemplateGroup =
    namespaceGroups.find((group) => group.namespaceId === templateNamespace) ?? null;

  function setLimit(period: KeyLimitPeriod, value: string): void {
    setLimitsError(null);
    setLimitDraftState({ ...limitDraft, [period]: value });
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setNameError(null);
    setLimitsError(null);
    setFormError(null);

    const trimmedName = name.trim();
    if (mode === 'edit' && trimmedName.length === 0) {
      setNameError(t('keys.form.name_required'));
      return;
    }

    if (mode === 'create') {
      const body: CreateApiKeyRequest = {};
      if (trimmedName.length > 0) body.name = trimmedName;
      if (limitsEditable) {
        const limits = parseLimitDraft(limitDraft, cnyPerUsd, t);
        if (!limits.ok) {
          setLimitsError(limits.message);
          return;
        }
        for (const period of KEY_LIMIT_PERIODS) {
          assignCreateLimitField(body, period.id, limits.value[period.id]);
        }
      }
      try {
        const result = await createM.mutateAsync(body);
        toast.success(t('keys.toast.created'));
        onCreated(result.key, result.summary.name);
        if (selectedTemplateGroup !== null) {
          try {
            await applyNamespaceM.mutateAsync({
              keyId: result.summary.id,
              namespaceId: selectedTemplateGroup.namespaceId,
            });
          } catch (err) {
            toast.error(t('keys.template.failure', { error: formatApiError(err, t) }));
          }
        }
        onClose();
      } catch (err) {
        setFormError(formatApiError(err, t));
      }
      return;
    }

    if (target === null) return;
    const body: UpdateApiKeyRequest = {};
    if (trimmedName !== (target.name ?? '')) {
      body.name = trimmedName;
    }
    if (limitsEditable) {
      const limits = parseLimitDraft(limitDraft, cnyPerUsd, t);
      if (!limits.ok) {
        setLimitsError(limits.message);
        return;
      }
      for (const period of KEY_LIMIT_PERIODS) {
        if (limitDraft[period.id] !== initialLimitDraft[period.id]) {
          assignUpdateLimitField(body, period.id, limits.value[period.id]);
        }
      }
    }
    if (Object.keys(body).length === 0) {
      onClose();
      return;
    }
    try {
      await updateM.mutateAsync({ id: target.id, body });
      toast.success(t('keys.toast.saved'));
      onClose();
    } catch (err) {
      setFormError(formatApiError(err, t));
    }
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      title={mode === 'create' ? t('keys.drawer.create_title') : t('keys.drawer.edit_title')}
      description={
        mode === 'create' ? t('keys.drawer.create_description') : t('keys.drawer.edit_description')
      }
      closeLabel={t('keys.drawer.close')}
      testId="keys-drawer"
    >
      <form
        noValidate
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
        className="flex flex-col gap-4"
      >
        <FormField
          label={t('keys.form.name')}
          fieldId="keys-form-name"
          required={mode === 'edit'}
          hint={mode === 'create' ? t('keys.form.name_hint') : undefined}
          error={nameError}
        >
          <Input
            id="keys-form-name"
            value={name}
            maxLength={120}
            disabled={pending}
            invalid={nameError !== null}
            aria-describedby={nameError !== null ? 'keys-form-name-error' : undefined}
            placeholder={t('keys.form.name_placeholder')}
            onChange={(event) => {
              setName(event.currentTarget.value);
              setNameError(null);
            }}
            data-testid="keys-form-name"
          />
        </FormField>

        {mode === 'create' ? (
          <FormField
            label={t('keys.form.template_label')}
            fieldId="keys-form-template-namespace"
            hint={
              selectedTemplateGroup !== null
                ? t('keys.form.template_hint', { count: selectedTemplateGroup.skus.length })
                : undefined
            }
          >
            <Select
              value={templateNamespace}
              onValueChange={(next) => {
                setTemplateNamespace(next);
                setFormError(null);
              }}
              options={[
                { value: TEMPLATE_NONE, label: t('keys.form.template_none') },
                ...namespaceGroups.map((group) => ({
                  value: group.namespaceId,
                  label: group.displayName,
                })),
              ]}
              placeholder={t('keys.form.template_none')}
              disabled={pending || catalogQ.isLoading || namespaceGroups.length === 0}
              ariaLabel={t('keys.form.template_label')}
              testId="keys-form-template-namespace"
            />
          </FormField>
        ) : null}

        <fieldset className="m-0 flex min-w-0 flex-col gap-1.5 border-0 p-0">
          <legend className="p-0 text-label text-paper-mid">{t('keys.form.limits_label')}</legend>
          <div className="grid grid-cols-2 gap-2">
            {KEY_LIMIT_PERIODS.map((period) => (
              <FormField
                key={period.id}
                label={t(period.labelKey)}
                fieldId={`keys-form-limit-${period.suffix}`}
              >
                <Input
                  id={`keys-form-limit-${period.suffix}`}
                  mono
                  inputMode="decimal"
                  value={limitDraft[period.id]}
                  disabled={pending || !limitsEditable}
                  placeholder={t(period.placeholderKey)}
                  onChange={(event) => setLimit(period.id, event.currentTarget.value)}
                  data-testid={`keys-form-limit-${period.suffix}`}
                />
              </FormField>
            ))}
          </div>
          {!limitsEditable ? (
            <p
              role="status"
              data-testid="keys-form-rate-notice"
              className="m-0 text-label text-warn"
            >
              {displayRateQ.isLoading
                ? t('keys.form.limits_loading')
                : t('keys.form.limits_rate_unavailable')}
            </p>
          ) : limitsError !== null ? (
            <p
              role="alert"
              data-testid="keys-form-limits-error"
              className="m-0 text-label text-bad"
            >
              {limitsError}
            </p>
          ) : (
            <p className="m-0 text-label text-paper-low">{t('keys.form.limits_hint')}</p>
          )}
        </fieldset>

        {formError !== null ? (
          <p role="alert" data-testid="keys-form-error" className="m-0 text-label text-bad">
            {formError}
          </p>
        ) : null}

        <div className="mt-1 flex items-center justify-end gap-2">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={pending}
            data-testid="keys-form-cancel"
          >
            {t('keys.form.cancel')}
          </Button>
          <Button type="submit" variant="primary" loading={pending} data-testid="keys-form-submit">
            {mode === 'create' ? t('keys.form.submit_create') : t('keys.form.submit_save')}
          </Button>
        </div>
      </form>
    </Drawer>
  );
}

interface KeyMappingsDrawerProps {
  readonly target: ApiKeySummary;
  readonly canManage: boolean;
  readonly onClose: () => void;
}

interface NamespaceMappingPanelProps {
  readonly keyId: string;
  readonly canManage: boolean;
  readonly groups: readonly SkuNamespaceGroup[];
  readonly autoNamespaces: readonly ApiKeyAutoModelNamespaceRecord[];
  readonly catalogUnavailable: boolean;
  readonly namespaceOperationsEnabled: boolean;
}

function NamespaceMappingPanel({
  keyId,
  canManage,
  groups,
  autoNamespaces,
  catalogUnavailable,
  namespaceOperationsEnabled,
}: NamespaceMappingPanelProps): JSX.Element {
  const t = useT();
  const applyM = useApplyKeyModelNamespace();
  const enableAutoM = useEnableKeyAutoModelNamespace();
  const disableAutoM = useDisableKeyAutoModelNamespace();
  const [selectedNamespaceId, setSelectedNamespaceId] = useState('');
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [operationConflicts, setOperationConflicts] = useState<
    readonly ApiKeyModelMappingConflict[]
  >([]);
  const [disableTarget, setDisableTarget] = useState<ApiKeyAutoModelNamespaceRecord | null>(null);

  const pending = applyM.isPending || enableAutoM.isPending || disableAutoM.isPending;
  const selectedGroup = groups.find((group) => group.namespaceId === selectedNamespaceId) ?? null;
  const enabledIds = new Set(autoNamespaces.map((record) => record.namespace_id));

  function groupLabel(namespaceId: string, fallbackSlug: string | null): string {
    return (
      groups.find((group) => group.namespaceId === namespaceId)?.displayName ??
      fallbackSlug ??
      namespaceId
    );
  }

  function skuLabel(skuId: string, fallbackName: string): string {
    for (const group of groups) {
      const sku = group.skus.find((candidate) => candidate.sku_id === skuId);
      if (sku !== undefined) return sku.sku_name;
    }
    return fallbackName;
  }

  function sourceLabel(source: 'manual' | 'automatic'): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function handleOperationError(err: unknown): void {
    if (err instanceof ModelMappingConflictError) {
      setOperationConflicts(err.conflicts);
      setError(null);
      return;
    }
    setOperationConflicts([]);
    setError(formatApiError(err, t));
  }

  async function applyCurrentModels(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      const result = await applyM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(
        t('keys.mappings.namespace.applied', {
          namespace: selectedGroup.displayName,
          count: String(result.applied_count),
        }),
      );
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function enableAutomaticMapping(): Promise<void> {
    if (selectedGroup === null) return;
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await enableAutoM.mutateAsync({ keyId, namespaceId: selectedGroup.namespaceId });
      setStatus(
        t('keys.mappings.namespace.auto_enabled', { namespace: selectedGroup.displayName }),
      );
    } catch (err) {
      handleOperationError(err);
    }
  }

  async function disableAutomaticMapping(record: ApiKeyAutoModelNamespaceRecord): Promise<void> {
    const label = groupLabel(record.namespace_id, record.namespace_slug);
    setError(null);
    setStatus(null);
    setOperationConflicts([]);
    try {
      await disableAutoM.mutateAsync({ keyId, namespaceId: record.namespace_id });
      setStatus(t('keys.mappings.namespace.auto_disabled', { namespace: label }));
      setDisableTarget(null);
    } catch (err) {
      setError(formatApiError(err, t));
    }
  }

  return (
    <section
      aria-labelledby="keys-mapping-namespace-title"
      className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well/30 p-4"
      data-testid="keys-mapping-namespace-panel"
    >
      <div className="flex flex-col gap-1">
        <h3 id="keys-mapping-namespace-title" className="m-0 text-section text-paper">
          {t('keys.mappings.namespace.title')}
        </h3>
        <p className="m-0 text-label text-paper-low">{t('keys.mappings.namespace.description')}</p>
      </div>

      {canManage ? (
        <div className="flex flex-col gap-2">
          <FormField
            label={t('keys.mappings.namespace.select_label')}
            fieldId="keys-mapping-namespace-action"
            hint={
              selectedGroup === null
                ? undefined
                : t('keys.mappings.namespace.selected_hint', {
                    count: String(selectedGroup.skus.length),
                  })
            }
          >
            <Select
              value={selectedNamespaceId}
              onValueChange={(next) => {
                setSelectedNamespaceId(next);
                setError(null);
                setStatus(null);
                setOperationConflicts([]);
              }}
              options={groups.map((group) => ({
                value: group.namespaceId,
                label: group.displayName,
              }))}
              placeholder={t('keys.mappings.namespace.select_placeholder')}
              disabled={pending || catalogUnavailable || groups.length === 0}
              ariaLabel={t('keys.mappings.namespace.select_label')}
              testId="keys-mapping-namespace-action"
            />
          </FormField>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={pending || selectedGroup === null || !namespaceOperationsEnabled}
              loading={applyM.isPending}
              onClick={() => {
                void applyCurrentModels();
              }}
              data-testid="keys-mapping-namespace-apply"
            >
              {t('keys.mappings.namespace.apply')}
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={
                pending ||
                selectedGroup === null ||
                enabledIds.has(selectedNamespaceId) ||
                !namespaceOperationsEnabled
              }
              loading={enableAutoM.isPending}
              onClick={() => {
                void enableAutomaticMapping();
              }}
              data-testid="keys-mapping-namespace-auto-enable"
            >
              {enabledIds.has(selectedNamespaceId)
                ? t('keys.mappings.namespace.auto_enabled_button')
                : t('keys.mappings.namespace.auto_enable')}
            </Button>
          </div>
        </div>
      ) : (
        <p
          role="status"
          className="m-0 text-label text-paper-mid"
          data-testid="keys-mappings-readonly"
        >
          {t('keys.mappings.readonly')}
        </p>
      )}

      {!namespaceOperationsEnabled ? (
        <p
          role="status"
          className="m-0 rounded border border-warn-line bg-warn-dim/40 px-3 py-2 text-label text-paper-mid"
          data-testid="keys-mapping-namespace-operations-disabled"
        >
          {t('keys.mappings.namespace.operations_disabled')}
        </p>
      ) : null}

      {operationConflicts.length > 0 ? (
        <section
          role="alert"
          className="flex flex-col gap-2 rounded border border-warn-line bg-warn-dim/40 px-3 py-2"
          data-testid="keys-mapping-namespace-conflicts"
        >
          <p className="m-0 text-label font-medium text-warn">
            {t('keys.mappings.conflicts.title')}
          </p>
          <p className="m-0 text-label text-paper-mid">
            {t('keys.mappings.conflicts.description')}
          </p>
          <ul className="m-0 list-none space-y-2 p-0">
            {operationConflicts.map((conflict) => (
              <li
                key={conflict.alias}
                className="rounded border border-warn-line bg-card px-3 py-2"
                data-testid={`keys-mapping-namespace-conflict-${conflict.alias}`}
              >
                <p className="m-0 font-mono text-dense text-paper">{conflict.alias}</p>
                <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-label text-paper-mid">
                  {conflict.candidates.map((candidate) => (
                    <li key={`${candidate.namespace_id}:${candidate.sku_id}`}>
                      <span className="font-mono">
                        {skuLabel(candidate.sku_id, candidate.sku_name)}
                      </span>
                      {' · '}
                      {candidate.sources.map(sourceLabel).join(', ')}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      <div className="flex flex-col gap-2">
        <p className="m-0 text-label text-paper-mid">
          {t('keys.mappings.namespace.enabled_title')}
        </p>
        {autoNamespaces.length === 0 ? (
          <p className="m-0 text-label text-paper-low" data-testid="keys-mapping-auto-empty">
            {t('keys.mappings.namespace.enabled_empty')}
          </p>
        ) : (
          <ul
            className="m-0 flex list-none flex-col gap-2 p-0"
            data-testid="keys-mapping-auto-list"
          >
            {autoNamespaces.map((record) => (
              <li
                key={record.namespace_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-line-soft bg-card px-3 py-2"
                data-testid={`keys-mapping-auto-${record.namespace_id}`}
              >
                <span className="font-mono text-dense text-paper">
                  {groupLabel(record.namespace_id, record.namespace_slug)}
                </span>
                {canManage ? (
                  <Button
                    variant="danger"
                    size="sm"
                    disabled={pending}
                    onClick={() => {
                      setDisableTarget(record);
                    }}
                    data-testid={`keys-mapping-auto-disable-${record.namespace_id}`}
                  >
                    {t('keys.mappings.namespace.auto_disable')}
                  </Button>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>

      {catalogUnavailable ? (
        <p role="alert" className="m-0 text-label text-bad">
          {t('keys.mappings.catalog_error')}
        </p>
      ) : null}
      {error !== null ? (
        <p
          role="alert"
          className="m-0 text-label text-bad"
          data-testid="keys-mapping-namespace-error"
        >
          {error}
        </p>
      ) : null}
      {status !== null ? (
        <p
          role="status"
          className="m-0 text-label text-ok"
          data-testid="keys-mapping-namespace-status"
        >
          {status}
        </p>
      ) : null}

      {disableTarget !== null ? (
        <ConfirmDialog
          open
          onOpenChange={(open) => {
            if (!open && !disableAutoM.isPending) setDisableTarget(null);
          }}
          tone="danger"
          title={t('keys.mappings.namespace.disable_title')}
          body={t('keys.mappings.namespace.disable_description', {
            namespace: groupLabel(disableTarget.namespace_id, disableTarget.namespace_slug),
          })}
          confirmLabel={t('keys.mappings.namespace.disable_confirm')}
          cancelLabel={t('keys.mappings.namespace.disable_cancel')}
          closeLabel={t('keys.mappings.namespace.disable_close')}
          loading={disableAutoM.isPending}
          testId="keys-mapping-auto-disable-confirm"
          onConfirm={() => {
            void disableAutomaticMapping(disableTarget);
          }}
        />
      ) : null}
    </section>
  );
}

/**
 * Per-key "Model mappings" drawer (epic #898 P6 #913). Lists the key's
 * alias → SKU mappings, adds new ones (alias input + namespace/SKU
 * cascade fed by `/v1/catalog/me` `skus[]`), re-PUTs an existing alias
 * to change its target, and deletes.
 *
 * Alias validation is inline next to the input — the platform rejects
 * aliases containing `/`, so the same rule is checked client-side and
 * any server-side alias rejection also lands on the field, never
 * toast-only.
 */
function KeyMappingsDrawer({ target, canManage, onClose }: KeyMappingsDrawerProps): JSX.Element {
  const t = useT();
  const toast = useToast();
  const mappingsQ = useKeyModelMappings(target.id);
  const catalogQ = useCatalog();
  const upsertM = useUpsertKeyModelMapping();
  const deleteM = useDeleteKeyModelMapping();

  const [editingAlias, setEditingAlias] = useState<string | null>(null);
  const [alias, setAlias] = useState('');
  const [namespaceId, setNamespaceId] = useState('');
  const [skuId, setSkuId] = useState('');
  const [aliasError, setAliasError] = useState<string | null>(null);
  const [skuError, setSkuError] = useState<string | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [mutationConflicts, setMutationConflicts] = useState<readonly ApiKeyModelMappingConflict[]>(
    [],
  );
  const [deleteConflictAlias, setDeleteConflictAlias] = useState<string | null>(null);

  const pending = upsertM.isPending || deleteM.isPending;
  const mappings = mappingsQ.data?.mappings ?? [];
  const autoNamespaces = mappingsQ.data?.auto_namespaces ?? [];
  const conflicts = mappingsQ.data?.conflicts ?? [];
  const displayedConflicts = [
    ...conflicts.filter(
      (conflict) => !mutationConflicts.some((candidate) => candidate.alias === conflict.alias),
    ),
    ...mutationConflicts,
  ];
  const skus = useMemo(() => catalogQ.data?.skus ?? [], [catalogQ.data?.skus]);
  const namespaceGroups = useMemo(() => groupSkusByNamespace(skus), [skus]);
  const skuById = useMemo(() => {
    const map = new Map<string, CatalogSku>();
    for (const sku of skus) map.set(sku.sku_id, sku);
    return map;
  }, [skus]);
  const selectedGroup = namespaceGroups.find((group) => group.namespaceId === namespaceId) ?? null;

  function resetForm(): void {
    setEditingAlias(null);
    setAlias('');
    setNamespaceId('');
    setSkuId('');
    setAliasError(null);
    setSkuError(null);
    setMutationConflicts([]);
  }

  function startEdit(mapping: ApiKeyModelMappingRecord): void {
    setEditingAlias(mapping.alias);
    setAlias(mapping.alias);
    setAliasError(null);
    setSkuError(null);
    setListError(null);
    setMutationConflicts([]);
    const sku = skuById.get(mapping.sku_id) ?? null;
    setNamespaceId(sku?.namespace_id ?? '');
    setSkuId(sku !== null ? sku.sku_id : '');
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setAliasError(null);
    setSkuError(null);
    setListError(null);

    const trimmedAlias = alias.trim();
    if (trimmedAlias.length === 0) {
      setAliasError(t('keys.mappings.alias_required'));
      return;
    }
    if (trimmedAlias.includes('/')) {
      setAliasError(t('keys.mappings.alias_no_slash'));
      return;
    }
    if (skuId.length === 0) {
      setSkuError(t('keys.mappings.sku_required'));
      return;
    }
    const isEdit = editingAlias !== null;
    setMutationConflicts([]);
    try {
      await upsertM.mutateAsync({ keyId: target.id, alias: trimmedAlias, skuId });
      toast.success(isEdit ? t('keys.mappings.toast.updated') : t('keys.mappings.toast.added'));
      resetForm();
    } catch (err) {
      if (err instanceof ModelMappingConflictError) {
        setMutationConflicts(err.conflicts);
        setAliasError(null);
        return;
      }
      // The platform's alias validation (no `/`, length) belongs to the
      // alias field; render it inline there instead of toast-only.
      setAliasError(formatApiError(err, t));
    }
  }

  async function handleDeleteAlias(aliasToDelete: string): Promise<void> {
    setListError(null);
    try {
      await deleteM.mutateAsync({ keyId: target.id, alias: aliasToDelete });
      toast.success(t('keys.mappings.toast.deleted'));
      setMutationConflicts((current) =>
        current.filter((conflict) => conflict.alias !== aliasToDelete),
      );
      if (editingAlias === aliasToDelete) resetForm();
      if (deleteConflictAlias === aliasToDelete) setDeleteConflictAlias(null);
    } catch (err) {
      setListError(formatApiError(err, t));
    }
  }

  function mappingTargetLabel(mapping: ApiKeyModelMappingRecord): string {
    // Catalog `sku_name` is brand-aliased for this console's brand; the
    // wire `sku_name` (canonical) is only a fallback for SKUs that have
    // left the org-visible catalog.
    return skuById.get(mapping.sku_id)?.sku_name ?? mapping.sku_name;
  }

  function sourceLabel(source: ApiKeyModelMappingRecord['source'] | undefined): string {
    return source === 'automatic'
      ? t('keys.mappings.source.automatic')
      : t('keys.mappings.source.manual');
  }

  function mappingSources(
    mapping: ApiKeyModelMappingRecord,
  ): readonly ApiKeyModelMappingRecord['source'][] {
    return mapping.sources?.length > 0 ? mapping.sources : [mapping.source ?? 'manual'];
  }

  function isManagedByAuto(mapping: ApiKeyModelMappingRecord): boolean {
    return mapping.managed_by_auto ?? mappingSources(mapping).includes('automatic');
  }

  return (
    <Drawer
      open
      onOpenChange={(open) => {
        if (!open && !pending) onClose();
      }}
      width="lg"
      title={t('keys.mappings.title')}
      description={t('keys.mappings.description', {
        key: target.name ?? (target.prefix.length > 0 ? `${target.prefix}…` : target.id),
      })}
      closeLabel={t('keys.drawer.close')}
      testId="keys-mappings-drawer"
    >
      <div className="flex flex-col gap-4">
        {mappingsQ.isLoading ? (
          <TableSkeleton rows={3} testId="keys-mappings-loading" />
        ) : mappingsQ.isError ? (
          <PanelError
            message={formatApiError(mappingsQ.error, t)}
            retryLabel={t('keys.retry')}
            onRetry={() => {
              void mappingsQ.refetch();
            }}
            testId="keys-mappings-load-error"
          />
        ) : (
          <>
            <NamespaceMappingPanel
              keyId={target.id}
              canManage={canManage}
              groups={namespaceGroups}
              autoNamespaces={autoNamespaces}
              catalogUnavailable={catalogQ.isError}
              namespaceOperationsEnabled={mappingsQ.data?.namespace_operations_enabled !== false}
            />

            {displayedConflicts.length > 0 ? (
              <section
                role="alert"
                className="flex flex-col gap-2 rounded-lg border border-warn-line bg-warn-dim/40 p-4"
                data-testid="keys-mapping-conflicts"
              >
                <h3 className="m-0 text-section text-warn">{t('keys.mappings.conflicts.title')}</h3>
                <p className="m-0 text-label text-paper-mid">
                  {t('keys.mappings.conflicts.description')}
                </p>
                <ul className="m-0 flex list-none flex-col gap-2 p-0">
                  {displayedConflicts.map((conflict) => {
                    const isTransient = mutationConflicts.some(
                      (candidate) => candidate.alias === conflict.alias,
                    );
                    const hasPersistedManual =
                      !isTransient &&
                      conflict.candidates.some((candidate) => candidate.sources.includes('manual'));
                    return (
                      <li
                        key={conflict.alias}
                        className="rounded border border-warn-line bg-card px-3 py-2"
                        data-testid={`keys-mapping-conflict-${conflict.alias}`}
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <p className="m-0 font-mono text-dense text-paper">{conflict.alias}</p>
                            <ul className="m-0 mt-1 list-disc space-y-1 pl-5 text-label text-paper-mid">
                              {conflict.candidates.map((candidate) => (
                                <li key={candidate.sku_id}>
                                  <span className="font-mono">
                                    {skuById.get(candidate.sku_id)?.sku_name ?? candidate.sku_name}
                                  </span>
                                  {' · '}
                                  {candidate.sources
                                    .map((source) => sourceLabel(source))
                                    .join(', ')}
                                </li>
                              ))}
                            </ul>
                          </div>
                          {canManage && hasPersistedManual ? (
                            <Button
                              variant="danger"
                              size="sm"
                              disabled={pending}
                              onClick={() => setDeleteConflictAlias(conflict.alias)}
                              data-testid={`keys-mapping-conflict-delete-${conflict.alias}`}
                            >
                              {t('keys.mappings.conflicts.delete_manual')}
                            </Button>
                          ) : null}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </section>
            ) : null}

            {mappings.length === 0 ? (
              <p
                className="m-0 rounded-lg border border-line-soft bg-well/50 px-4 py-6 text-center text-dense text-paper-low"
                data-testid="keys-mappings-empty"
              >
                {t('keys.mappings.empty')}
              </p>
            ) : (
              <Table testId="keys-mappings-table">
                <THead>
                  <Tr interactive={false}>
                    <Th>{t('keys.mappings.column.alias')}</Th>
                    <Th>{t('keys.mappings.column.target')}</Th>
                    <Th>{t('keys.mappings.column.source')}</Th>
                    {canManage ? <Th>{t('keys.column.actions')}</Th> : null}
                  </Tr>
                </THead>
                <TBody>
                  {mappings.map((mapping) => {
                    const automatic = isManagedByAuto(mapping);
                    return (
                      <Tr key={mapping.alias} data-testid={`keys-mapping-row-${mapping.alias}`}>
                        <Td mono emphasis heading>
                          {mapping.alias}
                        </Td>
                        <Td
                          mono
                          label={t('keys.mappings.column.target')}
                          data-testid={`keys-mapping-target-${mapping.alias}`}
                        >
                          {mappingTargetLabel(mapping)}
                        </Td>
                        <Td
                          label={t('keys.mappings.column.source')}
                          data-testid={`keys-mapping-source-${mapping.alias}`}
                        >
                          <span
                            className={`inline-flex rounded border px-2 py-0.5 text-micro ${
                              automatic
                                ? 'border-ok-line bg-ok-dim text-ok'
                                : 'border-line-soft bg-raised text-paper-mid'
                            }`}
                          >
                            {mappingSources(mapping)
                              .map((source) => sourceLabel(source))
                              .join(' + ')}
                          </span>
                        </Td>
                        {canManage ? (
                          <Td label={t('keys.column.actions')} stack>
                            {automatic ? (
                              <span className="text-label text-paper-low">
                                {t('keys.mappings.automatic_managed')}
                              </span>
                            ) : (
                              <div className="flex items-center justify-end gap-1.5 max-md:justify-start">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  disabled={pending}
                                  onClick={() => startEdit(mapping)}
                                  data-testid={`keys-mapping-edit-${mapping.alias}`}
                                >
                                  {t('keys.edit')}
                                </Button>
                                <Button
                                  variant="danger"
                                  size="sm"
                                  disabled={pending}
                                  onClick={() => {
                                    void handleDeleteAlias(mapping.alias);
                                  }}
                                  data-testid={`keys-mapping-delete-${mapping.alias}`}
                                >
                                  {t('keys.mappings.delete')}
                                </Button>
                              </div>
                            )}
                          </Td>
                        ) : null}
                      </Tr>
                    );
                  })}
                </TBody>
              </Table>
            )}
          </>
        )}

        {listError !== null ? (
          <p
            role="alert"
            data-testid="keys-mappings-list-error"
            className="m-0 text-label text-bad"
          >
            {listError}
          </p>
        ) : null}

        {canManage && !mappingsQ.isLoading && !mappingsQ.isError ? (
          <form
            noValidate
            onSubmit={(event) => {
              void handleSubmit(event);
            }}
            className="flex flex-col gap-3 rounded-lg border border-line-soft bg-well/30 p-4"
            data-testid="keys-mapping-form"
          >
            <p className="m-0 text-label text-paper-mid">
              {editingAlias !== null
                ? t('keys.mappings.edit_title', { alias: editingAlias })
                : t('keys.mappings.add_title')}
            </p>
            <FormField
              label={t('keys.mappings.alias_label')}
              fieldId="keys-mapping-alias"
              required
              hint={aliasError === null ? t('keys.mappings.alias_hint') : undefined}
              error={aliasError}
            >
              <Input
                id="keys-mapping-alias"
                mono
                value={alias}
                maxLength={200}
                disabled={pending || editingAlias !== null}
                invalid={aliasError !== null}
                placeholder={t('keys.mappings.alias_placeholder')}
                onChange={(event) => {
                  setAlias(event.currentTarget.value);
                  setAliasError(null);
                }}
                data-testid="keys-mapping-alias"
              />
            </FormField>
            <div className="grid grid-cols-2 gap-2 max-md:grid-cols-1">
              <FormField
                label={t('keys.mappings.namespace_label')}
                fieldId="keys-mapping-namespace"
                required
              >
                <Select
                  value={namespaceId}
                  onValueChange={(next) => {
                    setNamespaceId(next);
                    setSkuId('');
                    setSkuError(null);
                  }}
                  options={namespaceGroups.map((group) => ({
                    value: group.namespaceId,
                    label: group.displayName,
                  }))}
                  placeholder={t('keys.mappings.namespace_placeholder')}
                  disabled={pending || catalogQ.isLoading || catalogQ.isError}
                  ariaLabel={t('keys.mappings.namespace_label')}
                  testId="keys-mapping-namespace"
                />
              </FormField>
              <FormField
                label={t('keys.mappings.sku_label')}
                fieldId="keys-mapping-sku"
                required
                error={skuError}
              >
                <Select
                  value={skuId}
                  onValueChange={(next) => {
                    setSkuId(next);
                    setSkuError(null);
                  }}
                  options={(selectedGroup?.skus ?? []).map((sku) => ({
                    value: sku.sku_id,
                    label: skuDisplayLabel(sku),
                  }))}
                  placeholder={
                    namespaceId.length === 0
                      ? t('keys.mappings.sku_pick_namespace_first')
                      : t('keys.mappings.sku_placeholder')
                  }
                  disabled={
                    pending || catalogQ.isLoading || catalogQ.isError || namespaceId.length === 0
                  }
                  ariaLabel={t('keys.mappings.sku_label')}
                  testId="keys-mapping-sku"
                />
              </FormField>
            </div>
            {catalogQ.isError ? (
              <p role="alert" className="m-0 text-label text-bad">
                {t('keys.mappings.catalog_error')}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              {editingAlias !== null ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                  disabled={pending}
                  data-testid="keys-mapping-cancel-edit"
                >
                  {t('keys.mappings.cancel_edit')}
                </Button>
              ) : null}
              <Button
                type="submit"
                variant="primary"
                size="sm"
                loading={upsertM.isPending}
                disabled={catalogQ.isLoading}
                data-testid="keys-mapping-submit"
              >
                {editingAlias !== null
                  ? t('keys.mappings.submit_save')
                  : t('keys.mappings.submit_add')}
              </Button>
            </div>
          </form>
        ) : null}

        {deleteConflictAlias !== null ? (
          <ConfirmDialog
            open
            onOpenChange={(open) => {
              if (!open && !deleteM.isPending) setDeleteConflictAlias(null);
            }}
            tone="danger"
            title={t('keys.mappings.conflicts.delete_title')}
            body={t('keys.mappings.conflicts.delete_description', { alias: deleteConflictAlias })}
            confirmLabel={t('keys.mappings.conflicts.delete_confirm')}
            cancelLabel={t('keys.mappings.conflicts.delete_cancel')}
            closeLabel={t('keys.mappings.conflicts.delete_close')}
            loading={deleteM.isPending}
            testId="keys-mapping-conflict-delete-confirm"
            onConfirm={() => {
              void handleDeleteAlias(deleteConflictAlias);
            }}
          />
        ) : null}
      </div>
    </Drawer>
  );
}

interface KeyRevealDialogProps {
  readonly plaintext: string;
  readonly keyName: string | null;
  readonly onClose: () => void;
}

/**
 * Forced one-time reveal. Radix-level dismissal (backdrop / Esc / the
 * header X) is ignored until the key has been copied; the explicit
 * footer close is the acknowledged exit and always works.
 */
function KeyRevealDialog({ plaintext, keyName, onClose }: KeyRevealDialogProps): JSX.Element {
  const t = useT();
  const { copy, copied, error } = useClipboard();
  const [hasCopied, setHasCopied] = useState(false);

  async function handleCopy(): Promise<void> {
    const ok = await copy(plaintext);
    if (ok) setHasCopied(true);
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => {
        if (!open && hasCopied) onClose();
      }}
      title={t('keys.plaintext.title')}
      closeLabel={t('keys.plaintext.close')}
      testId="keys-plaintext-modal"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} data-testid="keys-plaintext-close">
            {t('keys.plaintext.close')}
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleCopy();
            }}
            data-testid="keys-plaintext-copy"
          >
            {copied || hasCopied
              ? t('keys.plaintext.copied_button')
              : t('keys.plaintext.copy_button')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <p
          role="alert"
          data-testid="keys-plaintext-warning"
          className="m-0 rounded border border-warn-line bg-warn-dim/50 px-3 py-2 text-dense leading-relaxed text-warn"
        >
          {t('keys.plaintext.warning')}
        </p>
        {keyName !== null && keyName.length > 0 ? (
          <p className="m-0 text-label text-paper-low">
            {t('keys.plaintext.name_hint', { name: keyName })}
          </p>
        ) : null}
        <p
          data-testid="keys-plaintext-value"
          className="m-0 select-all break-all rounded bg-well px-3 py-2.5 font-mono text-body text-paper"
        >
          {plaintext}
        </p>
        <p data-testid="keys-plaintext-copy-status" className="m-0 text-label text-paper-low">
          {copied || hasCopied ? t('keys.plaintext.copied') : t('keys.plaintext.copy_anywhere')}
        </p>
        {error !== null ? (
          <p
            role="alert"
            data-testid="keys-plaintext-copy-error"
            className="m-0 text-label text-bad"
          >
            {error}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}
